import { useEffect, useState } from 'react';

/**
 * 새 배포 직후 열려 있던 탭이 옛 청크를 못 받는 경우(동적 import 실패) — 사용자 잘못이 아니라
 * 한 번은 자동으로 새로고침해 조용히 넘긴다. 1분 안에 또 실패하면 자동 새로고침을 멈추고
 * 안내만 보인다(무한 루프 방지). 세 앱의 라우터 에러 화면이 같이 쓴다(콘솔은 ErrorScreen, 공개 사이트는 자체 화면).
 */

const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError|Loading chunk \d+ failed/i;
const RELOAD_KEY = 'bb:chunk-reload-at';
const RELOAD_COOLDOWN_MS = 60_000;

export function isChunkLoadError(error: unknown): boolean {
  const text = error instanceof Error ? `${error.name} ${error.message}` : String(error ?? '');
  return CHUNK_ERROR_RE.test(text);
}

/** 마지막 자동 새로고침 시각 — 저장소를 못 읽으면 null(자동 새로고침 안 함, 수동 안내만) */
function readReloadAt(): number | null {
  try {
    return Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
  } catch {
    return null;
  }
}

function markReloadNow(): boolean {
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

/** 청크 로드 실패면 한 번 자동 새로고침한다. 돌려주는 값 = 지금 새로고침을 걸었는지 */
export function useChunkAutoReload(error: unknown): boolean {
  const chunk = isChunkLoadError(error);
  const [reloading, setReloading] = useState(false);
  useEffect(() => {
    if (!chunk) return;
    const last = readReloadAt();
    if (last === null || Date.now() - last < RELOAD_COOLDOWN_MS) return;
    if (!markReloadNow()) return;
    setReloading(true);
    window.location.reload();
  }, [chunk]);
  return reloading;
}
